import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAppDispatch from 'hooks/useAppDispatch';
import { nanoid } from 'nanoid';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import Crypto from 'crypto-js';
import useGetGuidebookAccessUrl from 'hooks/guidebook/useGetGuidebookAccessUrl';
import { setAuth } from 'store/slices/auth';
import { getUserAction } from 'store/slices/user/sagas/userActions';
import API from 'services/API';
import { authEndpoints } from 'services/API.constants';
import { isGuidebookOnlyUser } from 'utils/auth/authUtils';
import { Location } from 'utils/windowUtils';
import useAppSelector from '../../hooks/useAppSelector';
import {
  clearRequestedPath,
  clearRequestedUrl,
} from '../../store/slices/login';
import useGetLoginErrorMessage from './useGetLoginErrorMessage';
import { subscriptionErrorParamName } from './Login.constants';

const PMP_WEB_CLIENT_ID = 'pmpWeb_LTqCnTD8xis8YTZD7jcAwsFPN24t9dNR';
const SELF_REDIRECT_URI = '/app/login/web';
const DEVICE_ID_KEY = 'pmp-auth-device-id';
const codeVerifier = nanoid(48);

const prepareAuthRequestData = ({
  // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  email,
  // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  password,
  // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  grantType,
  // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  searchParams,
  // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  isMobileApp,
}) => {
  const getCodeChallenge = () => {
    const codeVerifierHashed = Crypto.SHA256(codeVerifier).toString(
      Crypto.enc.Hex,
    );
    return window.btoa(codeVerifierHashed);
  };

  const deviceId = localStorage.getItem(DEVICE_ID_KEY) || uuidv4();
  if (!localStorage.getItem(DEVICE_ID_KEY)) {
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }

  return {
    password,
    login: email,
    grantType: searchParams.get('grantType') || grantType,
    scope: searchParams.get('scope') || 'FULL',
    state: searchParams.get('state') || uuidv4(),
    redirectUri: searchParams.get('redirectUri') || SELF_REDIRECT_URI,
    clientId: isMobileApp ? searchParams.get('clientId') : PMP_WEB_CLIENT_ID,
    b64CodeChallenge:
      searchParams.get('b64CodeChallenge') || getCodeChallenge(),
    codeChallengeMethod: searchParams.get('codeChallengeMethod') || 'S256',
    deviceId,
  };
};

const useLoginSubmit = () => {
  const [errorMessage, setErrorMessage] = useState<string | ReactNode>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // this is used to close the window after successful authentication from the mobile app
  const closeWindowOnAuthenticate = searchParams.get(
    'closeWindowOnAuthenticate',
  );
  const [isDesktopServiceProviderUser, setIsDesktopServiceProviderUser] =
    useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { requestedPath, requestedUrl } = useAppSelector(
    (state) => state.login,
  );
  const getLoginErrorMessage = useGetLoginErrorMessage();
  const { getGuidebookAccessUrl } = useGetGuidebookAccessUrl();
  const { t } = useTranslation();

  const clearErrors = () => {
    setErrorMessage(null);
  };

  useEffect(() => {
    if (searchParams.get('mfaCode')) {
      navigate('/login'); // shouldn't allow retrigger the 2fa flow when user refresh the page
    }
  }, []);

  function setError(message?: string | ReactNode) {
    setErrorMessage(message || t('pageLogin.error'));
    setIsSubmitting(false);
  }

  function authExchangeFlow({
    // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    redirectUri,
    // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    grantType,
    // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    code,
    // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    authRequestData,
    // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    rememberMe,
    // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    redirectUriSearchParam,
    // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    isMobileApp,
  }) {
    if (isMobileApp) {
      Location.change(redirectUri); // redirection for mobile app
    } else {
      API.post(authEndpoints.codeExchange, {
        grantType,
        clientId: PMP_WEB_CLIENT_ID,
        code,
        codeVerifier,
        redirectUri: authRequestData.redirectUri,
      })
        .then(({ data: authExchangeData }) => {
          const { accessToken, refreshToken } = authExchangeData || {};

          if (rememberMe) {
            localStorage.setItem('remember_me', 'true');
          } else {
            localStorage.removeItem('remember_me');
          }

          if (accessToken && (refreshToken || !rememberMe)) {
            // TODO verify if grantType should depend on rememberMe
            localStorage.setItem('access_token', accessToken);
            if (refreshToken) {
              localStorage.setItem('refresh_token', refreshToken);
            }

            if (isGuidebookOnlyUser(accessToken)) {
              getGuidebookAccessUrl().then(({ data, error }) => {
                if (error || !data?.url) {
                  setError();
                  return;
                }

                // GB-only access token cannot be kept in localStorage as it will cause infinite redirects when trying to access PMP with it
                localStorage.removeItem('access_token');

                const { url } = data;
                Location.change(url);
              });

              return;
            }

            dispatch(setAuth({ isAuthenticated: true }));

            if (closeWindowOnAuthenticate) {
              window.close();
              return;
            }

            // @ts-expect-error TS2554 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            dispatch(getUserAction());

            if (redirectUriSearchParam) {
              Location.change(decodeURIComponent(redirectUriSearchParam));
            } else if (requestedPath) {
              dispatch(clearRequestedPath());
              navigate(requestedPath);
            } else if (requestedUrl) {
              dispatch(clearRequestedUrl());
              Location.change(requestedUrl);
            }
          } else {
            setError();
          }
        })
        .catch(() => {
          setError();
        });
    }
  }

  // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  function processLogin({ email, password, rememberMe }) {
    const grantType = rememberMe ? 'REFRESH_TOKEN' : 'AUTHORIZATION_CODE';
    const isMobileApp = searchParams.get('clientId')?.startsWith('pmpMobile_');
    const redirectUriSearchParam = searchParams.get('redirectUri');

    const authRequestData = prepareAuthRequestData({
      email,
      password,
      grantType,
      searchParams,
      isMobileApp,
    });

    API.post(authEndpoints.authenticate, authRequestData)
      .then(({ data }) => {
        if (data?.status === 'SUCCESSFUL' || data?.status === 'MFA_CHALLENGE') {
          const { code, redirectUri, mfaCode, mfaCodeSentTo, mfaType } = data;

          if (mfaCode) {
            setSearchParams((params) => {
              params.set('mfaCode', mfaCode);
              params.set('mfaCodeSentTo', mfaCodeSentTo);
              params.set('mfaType', mfaType);
              return params;
            });

            setIsSubmitting(false);
            return;
          }

          authExchangeFlow({
            redirectUri,
            grantType,
            code,
            authRequestData,
            rememberMe,
            redirectUriSearchParam,
            isMobileApp,
          });
        } else {
          setError();
        }
      })
      .catch((e) => {
        const { response } = e;
        const { data } = response || {};
        const { apiErrorCode: errorCode, errorObject } = data || {};

        if (errorCode === 'SERVICE_PROVIDER_SHOULD_ONLY_USE_MOBILE_APP') {
          setIsDesktopServiceProviderUser(true);
          setIsSubmitting(false);
          return;
        }

        if (errorCode) {
          setError(
            getLoginErrorMessage({
              errorCode,
              ...(errorObject || {}),
            }),
          );
          return;
        }

        setError();
      });
  }

  const handleSubmitLogin = (values: {
    email: string;
    password: string;
    registrationToken: string;
    rememberMe: boolean;
    pinCode: string;
  }) => {
    setIsSubmitting(true);
    const { email, password, registrationToken, rememberMe, pinCode } = values;

    setSearchParams((params) => {
      params.delete(subscriptionErrorParamName);
      return params;
    });

    if (pinCode) {
      API.post(authEndpoints.mfaExchange, {
        mfaCode: searchParams.get('mfaCode'),
        mfaChallenge: pinCode,
      })
        .then(({ data }) => {
          const { code, redirectUri } = data;

          const grantType = rememberMe ? 'REFRESH_TOKEN' : 'AUTHORIZATION_CODE';
          const isMobileApp = searchParams
            .get('clientId')
            ?.startsWith('pmpMobile_');
          const redirectUriSearchParam = searchParams.get('redirectUri');

          authExchangeFlow({
            redirectUri,
            grantType,
            code,
            authRequestData: prepareAuthRequestData({
              email,
              password,
              grantType,
              searchParams,
              isMobileApp,
            }),
            rememberMe,
            redirectUriSearchParam,
            isMobileApp,
          });
        })
        .catch(() => {
          setError(t('pageLogin.anotherPinCodeExpected'));
        });

      return;
    }

    if (registrationToken) {
      API.post('web-app/register-user', {
        email,
        password,
        registrationToken,
      })
        .then(() => {
          processLogin({ email, password, rememberMe });
        })
        .catch(() => {
          setError(t('pageLogin.errorCreateAccount'));
        });
    } else {
      processLogin({ email, password, rememberMe });
    }
  };

  return {
    handleSubmitLogin,
    clearErrors,
    errorMessage,
    isDesktopServiceProviderUser,
    isSubmitting,
    setIsDesktopServiceProviderUser,
  };
};

export default useLoginSubmit;
